import { decimal, invertPrice, type LiquiditySegment } from "@auto-pools/core";
import type {
  ISeriesApi,
  ISeriesPrimitive,
  IPrimitivePaneView,
  IPrimitivePaneRenderer,
  SeriesAttachedParameter,
} from "lightweight-charts";

function themeColorWithAlpha(name: string, fallback: string, alpha: number) {
  const value =
    getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
  const match = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(value);
  if (!match) return value;
  return `rgba(${String(Number.parseInt(match[1], 16))}, ${String(Number.parseInt(match[2], 16))}, ${String(Number.parseInt(match[3], 16))}, ${String(alpha)})`;
}

export function orientSegments(
  segments: readonly LiquiditySegment[],
  inverted: boolean,
): LiquiditySegment[] {
  const result = segments
    .map((s) => {
      if (
        !/^(0|[1-9]\d*)$/.test(s.activeLiquidity) ||
        decimal(s.lowerPrice).units <= 0n ||
        decimal(s.lowerPrice).compare(decimal(s.upperPrice)) >= 0
      )
        throw new Error("Invalid liquidity interval");
      return inverted
        ? {
            lowerPrice: invertPrice(s.upperPrice),
            upperPrice: invertPrice(s.lowerPrice),
            activeLiquidity: s.activeLiquidity,
          }
        : s;
    })
    .sort((a, b) => decimal(a.lowerPrice).compare(decimal(b.lowerPrice)));
  if (
    result.some(
      (s, i) => i > 0 && decimal(result[i - 1].upperPrice).compare(decimal(s.lowerPrice)) !== 0,
    )
  )
    throw new Error("Discontinuous liquidity coverage");
  return result;
}

/** Average active L weighted by covered pixel height; adjacent intervals are not additive. */
export function liquidityBins(
  segments: readonly LiquiditySegment[],
  coordinate: (price: number) => number | null,
  height: number,
) {
  const mapped = segments
    .flatMap((s) => {
      const lo = coordinate(Number(s.lowerPrice)),
        hi = coordinate(Number(s.upperPrice));
      return lo === null || hi === null || !Number.isFinite(lo + hi)
        ? []
        : [
            {
              top: Math.max(0, Math.min(lo, hi)),
              bottom: Math.min(height, Math.max(lo, hi)),
              liquidity: BigInt(s.activeLiquidity),
            },
          ];
    })
    .filter((s) => s.bottom > s.top);
  const maximum = mapped.reduce((max, s) => (s.liquidity > max ? s.liquidity : max), 0n);
  const step = Math.max(4, Math.ceil(height / 80));
  const rows: { top: number; height: number; relative: number }[] = [];
  for (let top = 0; top < height; top += step) {
    let weight = 0n,
      total = 0n,
      start = Infinity,
      end = -Infinity;
    for (const s of mapped) {
      const a = Math.max(top, s.top),
        b = Math.min(top + step, s.bottom);
      if (b <= a) continue;
      const pixels = BigInt(Math.round((b - a) * 1_000_000));
      weight += pixels;
      total += s.liquidity * pixels;
      start = Math.min(start, a);
      end = Math.max(end, b);
    }
    if (weight > 0n)
      rows.push({
        top: start,
        height: end - start,
        relative:
          maximum === 0n ? 0 : Number((total * 1_000_000n) / (weight * maximum)) / 1_000_000,
      });
  }
  return rows;
}

export function fitPriceRange(values: readonly (string | undefined)[]) {
  const finite = values.map(Number).filter((v) => Number.isFinite(v) && v > 0);
  if (finite.length < 2) return null;
  const min = Math.min(...finite),
    max = Math.max(...finite);
  if (max <= min) return null;
  const padding = (max - min) / 3;
  return { from: Math.max(Number.MIN_VALUE, min - padding), to: max + padding };
}

export class LiquidityOverlay implements ISeriesPrimitive {
  private series: ISeriesApi<"Candlestick"> | undefined;
  private repaint: (() => void) | undefined;
  private segments: readonly LiquiditySegment[] = [];
  private lower: string | undefined;
  private upper: string | undefined;
  private readonly renderer: IPrimitivePaneRenderer = {
    draw: (target) => {
      target.useMediaCoordinateSpace(({ context: ctx, mediaSize: { width, height } }) => {
        const series = this.series;
        if (!series) return;
        const coordinate = (price: number) => series.priceToCoordinate(price);
        const lo = this.lower ? coordinate(Number(this.lower)) : null,
          hi = this.upper ? coordinate(Number(this.upper)) : null;
        if (lo !== null && hi !== null && Number.isFinite(lo + hi)) {
          ctx.fillStyle = themeColorWithAlpha("--range-from", "#a9e3cd", 0.18);
          ctx.fillRect(0, Math.max(0, hi), width, Math.min(height, lo) - Math.max(0, hi));
        }
        ctx.fillStyle = themeColorWithAlpha("--faint", "#8b9194", 0.34);
        for (const row of liquidityBins(this.segments, coordinate, height)) {
          const barWidth = width * 0.23 * row.relative;
          ctx.fillStyle = themeColorWithAlpha("--faint", "#8b9194", 0.34);
          ctx.fillRect(width - barWidth, row.top, barWidth, Math.max(0, row.height - 1));
          if (hi !== null && lo !== null) {
            const top = Math.max(row.top, hi);
            const bottom = Math.min(row.top + Math.max(0, row.height - 1), lo);
            if (bottom > top) {
              ctx.fillStyle = themeColorWithAlpha("--pos-bar", "#19b588", 0.34);
              ctx.fillRect(width - barWidth, top, barWidth, bottom - top);
            }
          }
        }
      });
    },
  };
  private readonly view: IPrimitivePaneView = {
    zOrder: () => "bottom",
    renderer: () => this.renderer,
  };
  attached({ series, requestUpdate }: SeriesAttachedParameter): void {
    this.series = series as ISeriesApi<"Candlestick">;
    this.repaint = requestUpdate;
  }
  detached(): void {
    this.series = undefined;
    this.repaint = undefined;
  }
  paneViews(): readonly IPrimitivePaneView[] {
    return [this.view];
  }
  update(segments: readonly LiquiditySegment[], lower?: string, upper?: string): void {
    this.segments = segments;
    this.lower = lower;
    this.upper = upper;
    this.repaint?.();
  }
}
